import { useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { MaintCardShell } from './MaintCardShell';
import { ConfirmApplyDialog } from './ConfirmApplyDialog';

export default function JobDiscountsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [applying, setApplying] = useState(false);

  const scan = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/maintenance/job-discounts/scan');
      setData(res.data);
      const all = {};
      res.data.jobs.forEach((j) => { all[j.job_id] = j.risk !== 'high'; }); // risky rows start unchecked
      setSelected(all);
    } catch (e) { toast.error(e.response?.data?.detail || 'Scan failed'); }
    finally { setLoading(false); }
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;
  const riskyCount = (data?.risky_job_ids || []).length;

  const apply = async () => {
    setApplying(true);
    try {
      const mapping = {};
      const selectedJobs = data.jobs.filter((j) => selected[j.job_id]);
      selectedJobs.forEach((j) => {
        mapping[j.job_id] = { discount_amount: j.proposed_discount_amount, total: j.proposed_total };
      });
      // Fix G (2026-08-31): backend now requires explicit confirm_risky for
      // any risk:"high" row — the admin already opted in by checking that
      // row's checkbox here and typing APPLY in the dialog below, so we pass
      // it through instead of having those rows silently rejected.
      const confirmRisky = selectedJobs.some((j) => j.risk === 'high');
      const res = await api.post('/admin/maintenance/job-discounts/apply', { mapping, confirm_risky: confirmRisky });
      toast.success(`${res.data.total_updated} job(s) corrected`);
      if (res.data.errors?.length > 0) {
        toast.warning(`${res.data.errors.length} rejected: ${res.data.errors.map((e) => `${e.job_number || e.job_id} (${e.reason})`).join('; ')}`);
      }
      setConfirmOpen(false);
      scan();
    } catch (e) { toast.error(e.response?.data?.detail || 'Apply failed'); }
    finally { setApplying(false); }
  };

  return (
    <MaintCardShell
      title="Job discounts not applied"
      description="Jobs converted from a discounted estimate/invoice whose total doesn't reflect its proportional share of the discount."
      testId="job-discounts"
      count={data ? data.total_affected : null}
      riskCount={riskyCount}
      loading={loading}
      onScan={scan}
    >
      {data && data.jobs.length > 0 && (
        <div className="space-y-3">
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {data.jobs.map((j) => (
              <div key={j.job_id} className={`border rounded-lg p-3 text-xs ${j.risk === 'high' ? 'border-red-200 bg-red-50' : 'border-gray-100'}`} data-testid={`job-discounts-row-${j.job_id}`}>
                <label className="flex items-start gap-2 cursor-pointer">
                  <Checkbox data-testid={`job-discounts-row-${j.job_id}-checkbox`} checked={!!selected[j.job_id]} onCheckedChange={(v) => setSelected((s) => ({ ...s, [j.job_id]: !!v }))} />
                  <div className="flex-1">
                    <p className="font-semibold text-gray-800">{j.job_number} {j.risk === 'high' && <span className="text-red-600 ml-1">⚠ HIGH RISK</span>}</p>
                    <p className="text-gray-600">Total: ${j.current_total} → ${j.proposed_total} · Discount: ${j.current_discount_amount} → ${j.proposed_discount_amount}</p>
                    {j.risk_reason && <p className="text-red-700 mt-1">{j.risk_reason}</p>}
                    {j.affected_commissions?.length > 0 && (
                      <div className="mt-1 text-gray-500">
                        Affected commissions: {j.affected_commissions.map((c) => `${c.user_name} ($${c.current_commission_amount}→$${c.proposed_commission_amount})`).join(', ')}
                      </div>
                    )}
                  </div>
                </label>
              </div>
            ))}
          </div>
          <Button data-testid="job-discounts-apply-btn" size="sm" disabled={selectedCount === 0} onClick={() => setConfirmOpen(true)} className="bg-red-600 hover:bg-red-700 text-white">
            Apply selected ({selectedCount})
          </Button>
        </div>
      )}
      {data && data.jobs.length === 0 && <p className="text-xs text-emerald-600">No discount mismatches found.</p>}

      <ConfirmApplyDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={apply}
        confirming={applying}
        title="Correct job discounts"
        summary={`This will update total + discount_amount on ${selectedCount} selected job(s). Review the HIGH RISK ones carefully — they affect commissions too.`}
      />
    </MaintCardShell>
  );
}
